/**
 * Customer-Scoped Media API Client
 *
 * Needed by the editor's ImageEditModal (loaded via symlink).
 * Routes through customer-scoped endpoints.
 *
 * IMPORTANT: Types and function signatures MUST match the dashboard's
 * api/media.ts exactly, since ImageEditModal imports from '../../api/media'.
 */

import { apiGet, apiPost, apiRequest } from './client';

// =============================================================================
// TYPES (must match dashboard/src/api/media.ts exactly)
// =============================================================================

export interface MediaItem {
  id: string;
  website_id: string;
  source: 'pexels' | 'unsplash' | 'upload' | 'generated';
  source_id: string | null;
  r2_key: string;
  thumbnail_r2_key: string | null;
  width: number | null;
  height: number | null;
  alt_text: string | null;
  title: string | null;
  original_filename: string | null;
  original_query: string | null;
  content_hash: string | null;
  is_active: number;
  used_in: string | null;
  usage_count: number;
  created_at: string;
  last_used_at: string | null;
  /** Public URL (computed by backend) */
  url: string;
  thumbnailUrl: string | null;
}

export interface PexelsPhoto {
  id: number;
  width: number;
  height: number;
  photographer: string;
  alt: string;
  avg_color: string;
  src: {
    original: string;
    large2x: string;
    large: string;
    medium: string;
    small: string;
    tiny: string;
  };
}

interface ListMediaResponse {
  media: MediaItem[];
  total: number;
}

interface UploadMediaResponse {
  media: MediaItem;
}

interface SearchPexelsResponse {
  photos: PexelsPhoto[];
  total_results: number;
  page: number;
  per_page: number;
}

interface SaveStockResponse {
  media: MediaItem;
}

// =============================================================================
// API FUNCTIONS (signatures match dashboard, routes are customer-scoped)
// =============================================================================

export function listMedia(
  _websiteId?: string,
  _options?: { source?: string; activeOnly?: boolean; limit?: number; offset?: number }
): Promise<ListMediaResponse> {
  return apiGet<ListMediaResponse>('/api/customer/editor/media');
}

export function searchPexels(
  _websiteId: string,
  query: string,
  options?: { perPage?: number; page?: number; orientation?: string }
): Promise<SearchPexelsResponse> {
  return apiPost<SearchPexelsResponse>('/api/customer/editor/media/search', {
    query,
    perPage: options?.perPage ?? 20,
    page: options?.page ?? 1,
    orientation: options?.orientation,
  });
}

export function saveStockImage(
  _websiteId: string,
  photo: PexelsPhoto,
  originalQuery: string
): Promise<SaveStockResponse> {
  return apiPost<SaveStockResponse>('/api/customer/editor/media/save-stock', {
    photo,
    originalQuery,
  });
}

export async function uploadMedia(
  _websiteId: string,
  file: File,
  altText?: string,
  title?: string
): Promise<UploadMediaResponse> {
  const formData = new FormData();
  formData.append('file', file);
  if (altText) formData.append('altText', altText);
  if (title) formData.append('title', title);

  return apiRequest<UploadMediaResponse>('/api/customer/editor/media/upload', {
    method: 'POST',
    body: formData,
    headers: {},
  });
}
